import { useEffect, useMemo, useState } from 'react'
import {
  type VersionInfo as VersionInfoType,
  getEnvironmentBadge,
  getFormattedBuildInfo,
  getFormattedVersion,
  getVersionInfo,
} from '@/utils'
import { type SessionPageViewMode, formatSessionLines, getSessionInfo } from '@/utils/session-info'
import { Badge } from '@/components/ui/badge'
import { Check, SquareDashed, Trash2 } from 'lucide-react'
import { useVisualDebug } from '@/hooks/use-visual-debug'
import { PurgeCheckpointDialog } from './components/purge-checkpoint-dialog'
import {
  resolveVersionBadgeColorClassName,
  resolveVersionBadgeVariant,
} from './helpers/version-badge-style'
import { usePurgeCheckpoint } from './hooks/use-purge-checkpoint'
import { useSessionClock } from './hooks/use-session-clock'

interface VersionInfoProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  showBuildInfo?: boolean
  showEnvironment?: boolean
  minimal?: boolean
  spreadId?: string
  /** 1-based spread index */
  currentSpread?: number
  totalSpreads?: number
  pageViewMode?: SessionPageViewMode
}

export const VersionInfo = ({
  position = 'bottom-right',
  showBuildInfo = false,
  showEnvironment = true,
  minimal = false,
  spreadId,
  currentSpread,
  totalSpreads,
  pageViewMode,
}: VersionInfoProps) => {
  const [versionInfo, setVersionInfo] = useState<VersionInfoType | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const { isPurgeDialogOpen, setIsPurgeDialogOpen, isPurging, handleContinuePurge } =
    usePurgeCheckpoint(spreadId)
  const visualDebug = useVisualDebug()

  useEffect(() => {
    const info = getVersionInfo()

    setVersionInfo(info)
  }, [])

  const jwtNow = useSessionClock(isExpanded)

  const expandedContent = useMemo(() => {
    const header = [
      `${getFormattedVersion()}`,
      showEnvironment && getEnvironmentBadge(),
      showBuildInfo && getFormattedBuildInfo(),
    ]
      .filter(Boolean)
      .join('\n')

    const session = getSessionInfo(jwtNow)
    const spreadCtx = {
      spreadId,
      currentSpread,
      totalSpreads,
      pageViewMode,
    }
    const sessionBlock = formatSessionLines(session, spreadCtx)

    return [header, '─────────────', sessionBlock].join('\n')
  }, [showBuildInfo, showEnvironment, spreadId, currentSpread, totalSpreads, pageViewMode, jwtNow])

  if (!versionInfo) {
    return null
  }

  const positionStyles = {
    'top-left': { top: '10px', left: '10px' },
    'top-right': { top: '10px', right: '10px' },
    'bottom-left': { bottom: '10px', left: '10px' },
    'bottom-right': { bottom: '10px', right: '10px' },
  }

  const handleClick = () => {
    if (!minimal) {
      setIsExpanded(!isExpanded)
    }
  }

  const renderMinimal = () => (
    <Badge
      variant={resolveVersionBadgeVariant(versionInfo)}
      size={minimal ? 'xs' : 'sm'}
      className={`fixed ${resolveVersionBadgeColorClassName(versionInfo)} font-mono font-medium cursor-pointer z-[9999] shadow-md transition-all select-none ${
        minimal ? '' : 'px-3 py-1.5'
      }`}
      style={positionStyles[position]}
    >
      {getFormattedVersion()}
      {showEnvironment && ` • ${versionInfo.environment.toUpperCase()}`}
    </Badge>
  )

  if (minimal) {
    return renderMinimal()
  }

  return (
    <>
      <Badge
        variant={resolveVersionBadgeVariant(versionInfo)}
        size={isExpanded ? 'lg' : 'sm'}
        className={`fixed ${resolveVersionBadgeColorClassName(versionInfo)} font-mono font-medium cursor-pointer z-[9999] shadow-md transition-all select-none ${
          isExpanded
            ? 'px-4 py-3 text-xs leading-snug break-words min-w-[200px] max-w-[min(90vw,42rem)] flex-col items-stretch'
            : 'px-3 py-1.5'
        }`}
        style={positionStyles[position]}
        onClick={handleClick}
        title={isExpanded ? 'Click to collapse' : 'Click to expand version info'}
      >
        {isExpanded ? (
          <>
            <span className="whitespace-pre-line">{expandedContent}</span>
            <span className="my-2 block border-t border-white/30" aria-hidden="true" />
            <button
              type="button"
              // The badge itself collapses on click, so a control inside it must not bubble.
              onClick={(event) => {
                event.stopPropagation()
                visualDebug.toggle()
              }}
              aria-pressed={visualDebug.enabled}
              className="flex w-full items-center gap-2 rounded px-1 py-1 text-left hover:bg-black/15 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-current"
              title="Draw geometric / visual / rotated / AABB boxes around every shape"
            >
              <SquareDashed className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="flex-1">Bounding boxes</span>
              {visualDebug.enabled && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
            </button>
            <button
              type="button"
              // The badge itself collapses on click, so a control inside it must not bubble.
              onClick={(event) => {
                event.stopPropagation()
                setIsPurgeDialogOpen(true)
              }}
              disabled={isPurging}
              className="flex w-full items-center gap-2 rounded px-1 py-1 text-left hover:bg-black/15 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-current disabled:pointer-events-none disabled:opacity-50"
              title="Purge this spread's checkpoint data"
            >
              <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="flex-1">Purge Checkpoint Data</span>
            </button>
          </>
        ) : (
          `${getFormattedVersion()} • ${versionInfo.environment.toUpperCase()}`
        )}
      </Badge>

      <PurgeCheckpointDialog
        open={isPurgeDialogOpen}
        onOpenChange={setIsPurgeDialogOpen}
        isPurging={isPurging}
        onContinue={handleContinuePurge}
      />
    </>
  )
}

export default VersionInfo
